import { useEffect, useState } from 'react'
import { Clock3, Save, Sliders } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/common/Button'
import HumanizationService, { type HumanizationBufferConfig } from '@/services/humanization.service'

type BufferConfig = HumanizationBufferConfig

const DEFAULT_CONFIG: BufferConfig = {
  read_base_s: 2,
  read_jitter_s: 1,
  ms_per_char: 80,
  think_prob: 0.3,
  sleep_start_h: 23,
  sleep_end_h: 8,
  sleep_multiplier: 3,
}

function SliderField({
  label,
  value,
  min,
  max,
  step = 0.1,
  unit = '',
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step?: number
  unit?: string
  onChange: (v: number) => void
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-[var(--text-secondary)]">{label}</span>
        <span className="font-medium text-[var(--text-primary)] tabular-nums">
          {value.toFixed(step < 1 ? 1 : 0)}
          {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--color-brand)]"
      />
    </div>
  )
}

export function BufferTab() {
  const [global, setGlobal] = useState<BufferConfig>(DEFAULT_CONFIG)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)

  function update(key: keyof BufferConfig, val: number) {
    setGlobal((prev) => ({ ...prev, [key]: val }))
  }

  useEffect(() => {
    let cancelled = false
    HumanizationService.get()
      .then((cfg) => {
        if (!cancelled) setGlobal(cfg)
      })
      .catch(() => {
        if (!cancelled) toast.error('Não foi possível carregar o buffer humanizado.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function handleSave() {
    setSaving(true)
    try {
      const updated = await HumanizationService.update(global)
      setGlobal(updated)
      toast.success('Buffer humanizado salvo.')
    } catch {
      toast.error('Erro ao salvar configuração.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4 pb-6">
      <div className="glass-card p-5 space-y-5">
        <div className="flex items-center gap-2">
          <Sliders size={18} className="text-[var(--color-brand)]" />
          <h3 className="text-base font-semibold text-[var(--text-primary)]">
            Buffer Global (Ciclo)
          </h3>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <SliderField
            label="Leitura base (s)"
            value={global.read_base_s}
            min={0.5}
            max={10}
            onChange={(v) => update('read_base_s', v)}
            unit="s"
          />
          <SliderField
            label="Jitter leitura (s)"
            value={global.read_jitter_s}
            min={0}
            max={5}
            onChange={(v) => update('read_jitter_s', v)}
            unit="s"
          />
          <SliderField
            label="Ms por caractere"
            value={global.ms_per_char}
            min={20}
            max={300}
            step={5}
            onChange={(v) => update('ms_per_char', v)}
            unit="ms"
          />
          <SliderField
            label="Prob. think time"
            value={global.think_prob}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => update('think_prob', v)}
          />
          <SliderField
            label="Início sono (UTC h)"
            value={global.sleep_start_h}
            min={0}
            max={23}
            step={1}
            onChange={(v) => update('sleep_start_h', v)}
            unit="h"
          />
          <SliderField
            label="Fim sono (UTC h)"
            value={global.sleep_end_h}
            min={0}
            max={23}
            step={1}
            onChange={(v) => update('sleep_end_h', v)}
            unit="h"
          />
          <SliderField
            label="Multiplicador sono"
            value={global.sleep_multiplier}
            min={1}
            max={10}
            step={0.5}
            onChange={(v) => update('sleep_multiplier', v)}
            unit="×"
          />
        </div>

        <div className="flex justify-end">
          <Button
            icon={<Save size={14} />}
            onClick={handleSave}
            loading={saving}
            disabled={loading}
          >
            Salvar configuração global
          </Button>
        </div>
      </div>

      <div className="stub-panel">
        <Clock3 size={30} className="text-[var(--color-brand)]" />
        <h3>Override por Remetente</h3>
        <p>
          Configure delays individuais por instância. Endpoint{' '}
          <code>/api/v1/humanization/nodes/{'{node_id}'}</code> em desenvolvimento.
        </p>
      </div>
    </div>
  )
}
